import * as THREE from 'three';
import { GameObject, ObjectType } from '../GameObject';
import { LODLevel } from '../../optimization/LODSystem';
import { Config } from '../../core/Config';
import { Quadtree } from '../../optimization/SpatialPartition';

/**
 * Dynamic movement states
 */
export enum MovementState {
  IDLE,      // Not moving
  MOVING,    // Currently moving
  DORMANT    // Temporarily static (optimization)
}

/**
 * Dynamic update priority for performance optimization
 */
export enum UpdatePriority {
  HIGH,      // Update every frame
  MEDIUM,    // Update every 2-3 frames
  LOW        // Update every 4+ frames
}

/**
 * Efficient implementation of a dynamic object that integrates with
 * the existing culling, LOD, and spatial partitioning systems.
 */
export class DynamicObject extends GameObject {
  // Movement properties with dirty flags
  private velocity: THREE.Vector3 = new THREE.Vector3();
  private acceleration: THREE.Vector3 = new THREE.Vector3();
  private targetPosition: THREE.Vector3 = new THREE.Vector3();
  private movementState: MovementState = MovementState.IDLE;
  private isDirty: boolean = false;

  // Performance optimization properties
  private updatePriority: UpdatePriority = UpdatePriority.MEDIUM;
  private updateCounter: number = 0;
  private updateFrequency: number = 1; // Default: update every frame
  private lastQuadtreeUpdateTime: number = 0;
  private quadtreeUpdateThreshold: number = Config.DYNAMIC_OBJECT_SETTINGS.QUADTREE_UPDATE_THRESHOLD;

  // Predictive movement for collision detection
  private previousPosition: THREE.Vector3 = new THREE.Vector3();
  private predictedNextPosition: THREE.Vector3 = new THREE.Vector3();
  
  // Reusable vectors to avoid garbage collection
  private static readonly tempVec3 = new THREE.Vector3();
  
  /**
   * Create a new dynamic object
   * @param name Object name
   */
  constructor(name: string = 'DynamicObject') {
    // Initialize as a DYNAMIC type
    super(name, ObjectType.DYNAMIC);
    
    // Store initial position for quadtree optimization
    this.previousPosition.copy(this.getPosition());
    this.predictedNextPosition.copy(this.getPosition());
    
    // Set update frequency based on priority
    this.setUpdatePriority(UpdatePriority.MEDIUM);
    
    console.log(`DynamicObject created: ${this.getName()} with update frequency: ${this.updateFrequency}`);
  }
  
  /**
   * Efficiently update the dynamic object based on velocity and acceleration
   * Implements time-sliced movement and optimization for quadtree updates
   * @param deltaTime Time since last frame
   */
  public override update(deltaTime: number): void {
    // Skip updates based on priority to optimize performance
    this.updateCounter++;
    if (this.updateCounter % this.updateFrequency !== 0) {
      return;
    }
    
    // Reset counter
    this.updateCounter = 0;
    
    // If dormant, skip movement updates but still update children
    if (this.movementState === MovementState.DORMANT) {
      super.update(deltaTime);
      return;
    }
    
    // Track if anything changed
    let positionChanged = false;
    
    // Store the previous position for quadtree optimization
    this.previousPosition.copy(this.getPosition());
    
    // Apply acceleration to velocity only if it's non-zero
    if (this.acceleration.lengthSq() > 0.00001) {
      this.velocity.add(DynamicObject.tempVec3.copy(this.acceleration).multiplyScalar(deltaTime));
      this.isDirty = true;
    }
    
    // Apply velocity to position only if it's non-zero
    const velocityLengthSq = this.velocity.lengthSq();
    if (velocityLengthSq > 0.00001) {
      // Get current position
      const position = this.getPosition();
      
      // Apply movement
      DynamicObject.tempVec3.copy(this.velocity).multiplyScalar(deltaTime);
      position.add(DynamicObject.tempVec3);
      
      // Enforce world boundaries with bouncing
      this.enforceWorldBoundaries(position);
      
      // Update position
      this.setPosition(position.x, position.y, position.z);
      positionChanged = true;
      
      // Update movement state
      this.movementState = MovementState.MOVING;
      this.isDirty = true;
      
      // Update predicted next position only when moving significantly
      if (velocityLengthSq > 0.1) {
        this.updatePredictedPosition(deltaTime);
      }
      
      // Check if we need to update in the quadtree
      if (positionChanged) {
        this.checkForQuadtreeUpdate();
      }
    } else if (this.movementState === MovementState.MOVING) {
      // We have stopped moving
      this.movementState = MovementState.IDLE;
      this.isDirty = true;
      
      // Force quadtree update when stopping
      this.lastQuadtreeUpdateTime = performance.now();
    }
    
    // If moving toward a target, check if we've arrived
    if (this.movementState === MovementState.MOVING && this.targetPosition.lengthSq() > 0.00001) {
      const distanceToTarget = this.getPosition().distanceTo(this.targetPosition);
      
      // If we're close enough to the target, stop
      if (distanceToTarget < 0.1) {
        this.setPosition(this.targetPosition.x, this.targetPosition.y, this.targetPosition.z);
        this.velocity.set(0, 0, 0);
        this.movementState = MovementState.IDLE;
        this.isDirty = true;
      }
    }
    
    // Update any children - we have to call super.update() regardless 
    // since we don't have direct access to the children property
    super.update(deltaTime);
  }
  
  /**
   * Check if the object has moved enough to warrant a quadtree update
   * Implements a threshold-based approach to avoid excessive updates
   */
  private checkForQuadtreeUpdate(): void {
    // Only check if we've moved enough
    const currentPosition = this.getPosition();
    const distanceMoved = currentPosition.distanceTo(this.previousPosition);
    
    // If we've moved past the threshold, mark for quadtree update
    if (distanceMoved > this.quadtreeUpdateThreshold) {
      // This is just a flag - actual update happens in SpatialManager
      this.lastQuadtreeUpdateTime = performance.now();
    }
  }
  
  /**
   * Update the predicted position for collision detection
   * @param deltaTime Time since last frame
   */
  private updatePredictedPosition(deltaTime: number): void {
    // Predict where we'll be next frame
    this.predictedNextPosition.copy(this.getPosition());
    DynamicObject.tempVec3.copy(this.velocity).multiplyScalar(deltaTime * Config.DYNAMIC_OBJECT_SETTINGS.MAX_PREDICTION_FRAMES);
    this.predictedNextPosition.add(DynamicObject.tempVec3);
  }
  
  /**
   * Set the velocity directly
   * @param x X component of velocity
   * @param y Y component of velocity
   * @param z Z component of velocity
   */
  public setVelocity(x: number, y: number, z: number): void {
    // Only update if velocity changes
    if (this.velocity.x !== x || this.velocity.y !== y || this.velocity.z !== z) {
      this.velocity.set(x, y, z);
      this.isDirty = true;
      
      // If velocity is zero, we're idle
      if (x === 0 && y === 0 && z === 0) {
        this.movementState = MovementState.IDLE;
      } else {
        this.movementState = MovementState.MOVING;
      }
    }
  }
  
  /**
   * Set the acceleration for physics-based movement
   * @param x X component of acceleration
   * @param y Y component of acceleration
   * @param z Z component of acceleration
   */
  public setAcceleration(x: number, y: number, z: number): void {
    if (this.acceleration.x !== x || this.acceleration.y !== y || this.acceleration.z !== z) {
      this.acceleration.set(x, y, z);
      this.isDirty = true;
    }
  }
  
  /**
   * Set the target position to move toward
   * @param x Target X position
   * @param y Target Y position
   * @param z Target Z position
   * @param speed Speed to move at (optional)
   */
  public moveTo(x: number, y: number, z: number, speed: number = 5): void {
    // Set target
    this.targetPosition.set(x, y, z);
    
    // Calculate direction to target
    const position = this.getPosition();
    DynamicObject.tempVec3.set(x, y, z).sub(position).normalize();
    
    // Set velocity based on direction and speed
    this.setVelocity(
      DynamicObject.tempVec3.x * speed,
      DynamicObject.tempVec3.y * speed,
      DynamicObject.tempVec3.z * speed
    );
    
    // Update state
    this.movementState = MovementState.MOVING;
    this.isDirty = true;
  }
  
  /**
   * Stop all movement immediately
   */
  public stop(): void {
    this.velocity.set(0, 0, 0);
    this.acceleration.set(0, 0, 0);
    this.targetPosition.set(0, 0, 0);
    this.movementState = MovementState.IDLE;
    this.isDirty = true;
  }
  
  /**
   * Set the object to dormant state to optimize performance
   * Dormant objects skip movement updates but can still have their
   * children updated
   * @param dormant Whether the object should be dormant
   */
  public setDormant(dormant: boolean): void {
    this.movementState = dormant ? MovementState.DORMANT : MovementState.IDLE;
  }
  
  /**
   * Set the update priority to control how frequently the object is updated
   * @param priority Update priority
   */
  public setUpdatePriority(priority: UpdatePriority): void {
    this.updatePriority = priority;
    
    // Set update frequency based on priority
    switch (priority) {
      case UpdatePriority.HIGH:
        this.updateFrequency = 1; // Every frame
        break;
      case UpdatePriority.MEDIUM:
        this.updateFrequency = 2; // Every 2 frames
        break;
      case UpdatePriority.LOW:
        this.updateFrequency = 4; // Every 4 frames
        break;
    }
  }
  
  /**
   * Get the current update priority
   */
  public getUpdatePriority(): UpdatePriority {
    return this.updatePriority;
  }
  
  /**
   * Get the last time the object's position was updated in the quadtree
   */
  public getLastQuadtreeUpdateTime(): number {
    return this.lastQuadtreeUpdateTime;
  }
  
  /**
   * Get the current movement state
   */
  public getMovementState(): MovementState {
    return this.movementState;
  }
  
  /**
   * Get the current velocity
   */
  public getVelocity(): THREE.Vector3 {
    return this.velocity.clone();
  }
  
  /**
   * Get the current acceleration
   */
  public getAcceleration(): THREE.Vector3 {
    return this.acceleration.clone();
  }
  
  /**
   * Get the predicted next position (for collision detection)
   */
  public getPredictedPosition(): THREE.Vector3 {
    return this.predictedNextPosition.clone();
  }
  
  /**
   * Check if the object state has changed since the last update
   */
  public isDirtyState(): boolean {
    return this.isDirty;
  }
  
  /**
   * Reset the dirty flag after processing state changes
   */
  public resetDirtyFlag(): void {
    this.isDirty = false;
  }
  
  /**
   * Get the previous position (before the current update)
   * Used by the SpatialManager for optimizing quadtree updates
   */
  public getPreviousPosition(): THREE.Vector3 {
    return this.previousPosition.clone();
  }
  
  /**
   * Get the current quadtree node that contains this object
   * Useful for spatial queries and debugging
   */
  public getCurrentQuadtreeNode(): any {
    return Quadtree.getContainingNode(this.getId());
  }
  
  /**
   * Enforce world boundaries with bouncing behavior
   * @param position The current position to check and modify if needed
   */
  private enforceWorldBoundaries(position: THREE.Vector3): void {
    const worldSize = Config.TERRAIN_SIZE;
    const halfSize = worldSize / 2;
    const bounceFactor = 0.8; // Reduce velocity on bounce for realism
    let bounced = false;
    
    // Check X boundaries with a safety margin
    const margin = 0.01; // Small margin to prevent edge cases
    
    // Check X boundaries
    if (position.x < -halfSize + margin) {
      position.x = -halfSize + margin;
      if (this.velocity.x < 0) {
        this.velocity.x *= -bounceFactor;
        bounced = true;
      }
    } else if (position.x > halfSize - margin) {
      position.x = halfSize - margin;
      if (this.velocity.x > 0) {
        this.velocity.x *= -bounceFactor;
        bounced = true;
      }
    }
    
    // Check Z boundaries
    if (position.z < -halfSize + margin) {
      position.z = -halfSize + margin;
      if (this.velocity.z < 0) {
        this.velocity.z *= -bounceFactor;
        bounced = true;
      }
    } else if (position.z > halfSize - margin) {
      position.z = halfSize - margin;
      if (this.velocity.z > 0) {
        this.velocity.z *= -bounceFactor;
        bounced = true;
      }
    }
    
    // Extra safety check: Force the position to be within bounds regardless
    position.x = Math.max(-halfSize + margin, Math.min(halfSize - margin, position.x));
    position.z = Math.max(-halfSize + margin, Math.min(halfSize - margin, position.z));
    
    // If we bounced, force a quadtree update
    if (bounced) {
      this.isDirty = true;
      this.lastQuadtreeUpdateTime = performance.now();
      
      // Slow down very fast objects to prevent them from passing through boundaries
      const maxSpeed = 20.0;
      const currentSpeed = this.velocity.length();
      if (currentSpeed > maxSpeed) {
        this.velocity.multiplyScalar(maxSpeed / currentSpeed);
      }
    }
  }
}
